/**
 * @fileoverview Centralized React contexts for the shell module. All app-level
 * contexts are defined here with proper default values and null-safety.
 */

import * as React from 'react';
import type { Authentication, Branding, Navigation, PaletteModeContextValue, Router, Session } from '../types';

// ============================================================================
// Core Application Contexts
// ============================================================================

/**
 * Context for application branding configuration.
 */
export const BrandingContext = React.createContext<Branding | null>(null);

/**
 * Context for navigation tree structure.
 */
export const NavigationContext = React.createContext<Navigation>([]);

/**
 * Context for router implementation.
 */
export const RouterContext = React.createContext<Router | null>(null);

/**
 * Context for window reference, useful for SSR and iframe scenarios.
 */
export const WindowContext = React.createContext<Window | undefined>(undefined);

// ============================================================================
// Authentication Contexts
// ============================================================================

/**
 * Context for authentication actions (signIn, signOut).
 */
export const AuthenticationContext = React.createContext<Authentication | null>(null);

/**
 * Context for current user session.
 */
export const SessionContext = React.createContext<Session | null>(null);

// ============================================================================
// Theme Contexts
// ============================================================================

/**
 * Context for palette mode management (light/dark theme switching).
 */
export const PaletteModeContext = React.createContext<PaletteModeContextValue>({
  paletteMode: 'light',
  setPaletteMode: () => {},
  togglePaletteMode: () => {},
  isDualTheme: false,
});

